import { useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowUpIcon, Maximize2Icon, Minimize2Icon } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import Placeholder from "@tiptap/extension-placeholder";

import { Column, RichTextEditorInput, Row } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";

import { useInputDraft } from "@/hooks/useInputDraft";
import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { useCreateAddUserNote } from "@/hooks/createAdds/useCreateAddUserNote";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { tiptap } from "@/lib/tiptap";

// Stable reference: RichTextEditorInput re-parses content whenever the
// extensions array identity changes.
const composerExtensions = [
  ...tiptap.generateBaseExtensions(),
  Placeholder.configure({ placeholder: "Leave a note..." }),
];

const createNoteSchema = z.object({
  content: z.string().min(1, "Required"),
});

const COMPOSER_NON_EDITOR_HEIGHT_PX = 90;
const COMPOSER_BOTTOM_GAP_PX = 28;
const COMPACT_EDITOR_HEIGHT_PX = 140;

export const NoteComposer = ({
  companyName,
  jobTitle,
  userId,
}: {
  companyName: string;
  jobTitle: string;
  userId: number;
}) => {
  /* Database CRUD Operations */
  // Create
  const { mutate: createNote, isPending } = useCreateAddUserNote();

  /* React Hooks */
  const currentUser = useGetCurrentUser();
  const composerRef = useRef<HTMLFormElement>(null);
  const [expandedEditorHeight, setExpandedEditorHeight] = useState<number>();
  // An unsent note survives closing the sheet; cleared once the note saves.
  const { clearDraft, draft, setDraft } = useInputDraft(`note:${userId}`);
  const form = useForm<z.infer<typeof createNoteSchema>>({
    defaultValues: { content: draft },
    resolver: zodResolver(createNoteSchema),
  });

  /* Handlers */
  const handleSubmit = (data: z.infer<typeof createNoteSchema>) => {
    if (isPending || !currentUser) return;

    mixpanelUtils.trackFormSubmission(
      "Create Note",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );
    createNote(
      {
        companyName,
        content: data.content,
        jobTitle,
        ownerId: currentUser.id,
        userId,
      },
      {
        onSuccess: () => {
          clearDraft();
          form.reset({ content: "" });
        },
      },
    );
  };

  const handleToggleExpanded = () => {
    if (expandedEditorHeight !== undefined) {
      setExpandedEditorHeight(undefined);
      return;
    }

    const composerTop = composerRef.current?.getBoundingClientRect().top;
    if (composerTop === undefined) return;
    setExpandedEditorHeight(
      Math.max(
        COMPACT_EDITOR_HEIGHT_PX,
        window.innerHeight -
          composerTop -
          COMPOSER_NON_EDITOR_HEIGHT_PX -
          COMPOSER_BOTTOM_GAP_PX,
      ),
    );
  };

  /* Render */
  const render = () => {
    return (
      <Form {...form}>
        <form
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={form.handleSubmit(handleSubmit)}
          ref={composerRef}
        >
          <Column className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <FormField
              control={form.control}
              name="content"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <RichTextEditorInput
                      containerClassName="rounded-none border-0"
                      editorContentClasses="h-[140px] px-2 py-1 text-sm"
                      editorHeight={expandedEditorHeight}
                      extensions={composerExtensions}
                      mode={{
                        onChange: (value: string) => {
                          field.onChange(value);
                          setDraft(value);
                        },
                        type: "html",
                        value: field.value,
                      }}
                      onKeyDown={(event) => {
                        if (event.metaKey && event.key === "Enter") {
                          event.preventDefault();
                          mixpanelUtils.trackKeyboardShortcutUsed(
                            "Cmd+Enter (Create Note)",
                            MIXPANEL_SCREENS.CANDIDATE_SHEET,
                          );
                          void form.handleSubmit(handleSubmit)();
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage className="px-3" />
                </FormItem>
              )}
            />
            <Row className="items-center justify-between px-3 pb-3">
              <Button
                className="h-8 w-8 text-slate-400 hover:text-slate-700"
                onClick={handleToggleExpanded}
                size="icon"
                title={
                  expandedEditorHeight === undefined
                    ? "Expand note editor"
                    : "Collapse note editor"
                }
                type="button"
                variant="ghost"
              >
                {expandedEditorHeight === undefined ? (
                  <Maximize2Icon className="h-4 w-4" strokeWidth={1.5} />
                ) : (
                  <Minimize2Icon className="h-4 w-4" strokeWidth={1.5} />
                )}
              </Button>
              <Button
                className="h-8 w-8 text-slate-500 hover:text-slate-900"
                disabled={!form.watch("content") || isPending}
                size="icon"
                type="submit"
                variant="ghost"
              >
                <ArrowUpIcon className="h-4 w-4" strokeWidth={2} />
              </Button>
            </Row>
          </Column>
        </form>
      </Form>
    );
  };

  return render();
};
